/**
 * AssetLedger Pro — API Client
 *
 * Configured Axios instance with:
 * - Base URL from environment
 * - Auth token injection (Bearer header)
 * - Automatic token refresh on 401
 * - Global error normalisation
 * - Request/response logging in development
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/auth.store';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

// ── Create instance ─────────────────────────────────────────────────────────

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,          // Required to send/receive httpOnly cookies (refresh token)
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,                // 30 second timeout
});

// ── Request interceptor — attach access token ────────────────────────────────

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — handle token refresh on 401 ──────────────────────

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If 401 and we haven't retried yet — attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for auth endpoints to avoid infinite loops
      if (originalRequest.url?.includes('/auth/')) {
        useAuthStore.getState().logout();
        return Promise.reject(normaliseError(error));
      }

      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken: string = response.data.data.accessToken;
        useAuthStore.getState().setAccessToken(newToken);

        // Resolve all queued requests with new token
        refreshQueue.forEach(({ resolve }) => resolve(newToken));
        refreshQueue = [];

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);

      } catch (refreshError) {
        // Refresh failed — clear auth and redirect to login
        refreshQueue.forEach(({ reject }) => reject(refreshError));
        refreshQueue = [];
        useAuthStore.getState().logout();
        window.location.href = '/login?reason=session_expired';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(normaliseError(error));
  }
);

// ── Error normalisation ──────────────────────────────────────────────────────

export interface ApiError {
  message: string;
  code: string;
  details?: unknown;
  statusCode: number;
}

export function normaliseError(error: AxiosError): ApiError {
  const response = error.response?.data as any;
  return {
    message: response?.error ?? error.message ?? 'An unexpected error occurred',
    code: response?.code ?? 'UNKNOWN_ERROR',
    details: response?.details,
    statusCode: error.response?.status ?? 0,
  };
}

export function isApiError(err: unknown): err is ApiError {
  return typeof err === 'object' && err !== null && 'code' in err && 'statusCode' in err;
}
